import React, { useEffect, useState } from 'react';

interface Props {
  /** Valor em número (ex: 1234.56). null = vazio. */
  value: number | null;
  /** Callback com o número parseado (ou null). */
  onChange: (value: number | null) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  /** Erro renderiza borda rose. */
  hasError?: boolean;
}

/**
 * Input de valor monetário (R$) com máscara automática enquanto digita.
 *
 * <para>UX padrão de apps brasileiros: usuário digita só dígitos, o input
 * formata em tempo real como centavos. Ex: digitar <c>12345</c> mostra
 * <c>R$ 123,45</c>; digitar mais 1 dígito vira <c>R$ 1.234,56</c>.</para>
 *
 * <para>Internamente o estado externo (<c>value</c>) é sempre number — não
 * precisa parse extra no formulário pai. Backspace remove 1 dígito (e o
 * decimal "recoloca" automaticamente).</para>
 *
 * <para>Plug-and-play em qualquer form que precise de R$. Reuso:
 * MetasCompraPage, Reposição (recálculo manual em fase futura), etc.</para>
 */
const InputMoeda: React.FC<Props> = ({
  value,
  onChange,
  className,
  placeholder = 'R$ 0,00',
  disabled = false,
  id,
  ariaLabel,
  hasError = false,
}) => {
  const [texto, setTexto] = useState<string>(value != null ? formatar(value) : '');

  // Sincroniza se o pai mudar `value` externamente (ex: edição populando form).
  useEffect(() => {
    setTexto(value != null ? formatar(value) : '');
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Mantém só dígitos — vírgula e separadores são reaplicados.
    const raw = e.target.value.replace(/\D/g, '');
    if (raw === '') {
      setTexto('');
      onChange(null);
      return;
    }
    const cents = parseInt(raw, 10);
    const novoValor = cents / 100;
    setTexto(formatar(novoValor));
    onChange(novoValor);
  };

  // Seleciona o conteúdo no foco — operador troca valor inteiro com 1 digitação.
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={texto}
      onChange={handleChange}
      onFocus={handleFocus}
      placeholder={placeholder}
      disabled={disabled}
      id={id}
      aria-label={ariaLabel}
      className={
        className ??
        `h-9 w-full rounded-md border bg-white px-2 text-sm tabular-nums dark:bg-zinc-950 dark:text-zinc-100 ${
          hasError ? 'border-rose-400 dark:border-rose-700' : 'border-zinc-200 dark:border-zinc-700'
        }`
      }
    />
  );
};

function formatar(n: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export default InputMoeda;
